import React from 'react';

import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { SecurityIcon } from 'components/Icons';
import { getMFAError } from 'containers/UserProfile/mfaUtils';

import { OrgConfigBox } from './OrgConfigBox';
import { useMFAPolicy } from './useMFAPolicy';
import { renderOrgSettingHeader } from './utils';

// The server authorizes changes from live organization-admin membership, independently of this UI.
export const TwoFactorPolicy = () => {
  const navigate = useNavigate();
  const {
    data,
    error,
    isPending,
    refetch,
    required,
    setRequired,
    saving,
    saveError,
    saved,
    setSaved,
    save,
    unchanged,
    needsRepair,
    needsReauthentication,
    reauthenticate,
  } = useMFAPolicy();

  return (
    <OrgConfigBox>
      <Stack spacing={4}>
        {renderOrgSettingHeader({
          icon: <SecurityIcon />,
          header: 'Two-factor authentication',
          subHeader:
            'Require all users in your organization to use an authenticator app when signing in, including users signing in with SSO.',
        })}
        {isPending && <Text role="status">Loading policy…</Text>}
        {!!(error || saveError) && (
          <Alert status="error">
            <AlertIcon />
            {getMFAError(saveError || error)}
          </Alert>
        )}
        {saved && (
          <Alert status="success">
            <AlertIcon />
            Two-factor authentication policy updated.
          </Alert>
        )}
        {data && (
          <>
            <Checkbox
              isChecked={required}
              isDisabled={!data.can_manage_policy || saving}
              onChange={event => {
                setRequired(event.target.checked);
                setSaved(false);
              }}
            >
              Require two-factor authentication for all users
            </Checkbox>
            {!data.can_manage_policy && (
              <Text>
                Only organization administrators can change this policy.
              </Text>
            )}
            {needsRepair && (
              <Alert status="warning">
                <AlertIcon />
                Enforcement setup is incomplete. An organization administrator
                must reapply the policy.
              </Alert>
            )}
            {data.can_manage_policy && (
              <>
                {required && !data.enabled && (
                  <Alert status="info">
                    <AlertIcon />
                    Enable two-factor authentication for your own account before
                    requiring it for everyone.
                  </Alert>
                )}
                {required && (!unchanged || needsRepair) && (
                  <Text>
                    Applying this policy signs everyone out, including you.
                    Users without an authenticator must complete setup when they
                    sign in again. Scripts that sign in with a username and
                    password must also supply a one-time code.
                  </Text>
                )}
                <HStack>
                  {required && !data.enabled ? (
                    <Button
                      onClick={() => navigate('/user-profile?tab=security')}
                    >
                      Set up my authenticator
                    </Button>
                  ) : (
                    <Button
                      bg={customTheme.colors.primary}
                      color="white"
                      _hover={{ opacity: 0.85 }}
                      isLoading={saving}
                      isDisabled={!!error || (unchanged && !needsRepair)}
                      onClick={save}
                    >
                      {needsRepair ? 'Reapply policy' : 'Save policy'}
                    </Button>
                  )}
                  {needsReauthentication && !error && (
                    <Button onClick={reauthenticate}>Sign in again</Button>
                  )}
                </HStack>
              </>
            )}
          </>
        )}
        {error && (
          <Button
            onClick={needsReauthentication ? reauthenticate : () => refetch()}
          >
            {needsReauthentication ? 'Sign in again' : 'Retry'}
          </Button>
        )}
      </Stack>
    </OrgConfigBox>
  );
};
